/**
 * Pestaña SEDES (campus) + AULAS (room). CRUD local-first → write-path (authz
 * owner/org_admin para sedes; campus_admin sobre su propia sede para aulas).
 */
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TextField } from "@/components/ui/text-field";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { Building2, DoorOpen, Plus, Trash2 } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import type { Campus } from "@space/powersync";
import {
  createCampusMutation,
  createRoomMutation,
  deleteCampusMutation,
  deleteRoomMutation,
} from "@space/shared";
import { useAcademyWrite, useCampuses, useRooms } from "../hooks/use-academy-data";

function RoomsManager(props: { orgId: string; campusId: string }) {
  const { t } = useI18n();
  const rooms = useRooms(() => props.campusId);
  const { drizzle } = useAcademyWrite();
  const [name, setName] = createSignal("");
  const [capacity, setCapacity] = createSignal("");

  const addRoom = async (e: Event) => {
    e.preventDefault();
    const db = drizzle();
    if (!db || !name().trim()) {
      return;
    }
    try {
      await createRoomMutation(db, props.orgId, props.campusId, {
        name: name(),
        capacity: Number(capacity()) || 0,
      });
      setName("");
      setCapacity("");
    } catch (err) {
      console.error("Error creating room:", err);
      showToast(t(msg`Error creating room`), "error");
    }
  };

  const removeRoom = async (roomId: string) => {
    const db = drizzle();
    if (!db) {
      return;
    }
    try {
      await deleteRoomMutation(db, roomId);
    } catch (err) {
      console.error("Error deleting room:", err);
    }
  };

  return (
    <div class="mt-3 space-y-2 border-t pt-3">
      <div class="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <DoorOpen class="h-3.5 w-3.5" /> {t(msg`Rooms`)}
      </div>
      <For each={rooms.data ?? []}>
        {(r) => (
          <div class="flex items-center justify-between rounded-md bg-muted/40 px-3 py-1.5 text-sm">
            <span>
              {r.name}
              <Show when={(r.capacity ?? 0) > 0}>
                <span class="ml-2 text-xs text-muted-foreground">
                  {(() => {
                    const cap = r.capacity ?? 0;
                    return t(msg`cap. ${cap}`);
                  })()}
                </span>
              </Show>
            </span>
            <Button variant="ghost" size="icon" class="h-7 w-7" onClick={() => removeRoom(r.id)}>
              <Trash2 class="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </For>
      <form onSubmit={addRoom} class="flex gap-2">
        <TextField
          class="flex-1"
          placeholder={t(msg`Room name`)}
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
        />
        <TextField
          type="number"
          inputmode="numeric"
          class="w-24"
          placeholder={t(msg`Cap.`)}
          value={capacity()}
          onInput={(e) => setCapacity(e.currentTarget.value)}
        />
        <Button type="submit" size="icon" variant="outline" disabled={!name().trim()}>
          <Plus class="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

function CampusCard(props: { campus: Campus; orgId: string }) {
  const { t } = useI18n();
  const { drizzle } = useAcademyWrite();

  const removeCampus = async () => {
    const db = drizzle();
    if (!db) {
      return;
    }
    try {
      await deleteCampusMutation(db, props.campus.id);
      showToast(t(msg`Campus removed`), "success");
    } catch (err) {
      console.error("Error deleting campus:", err);
      showToast(t(msg`Error removing campus`), "error");
    }
  };

  return (
    <div class="rounded-lg border p-4" data-testid="campus-card">
      <div class="flex items-start justify-between">
        <div class="flex items-center gap-2">
          <Building2 class="h-4 w-4 text-muted-foreground" />
          <div>
            <div class="font-semibold">{props.campus.name}</div>
            <Show when={props.campus.city}>
              <div class="text-xs text-muted-foreground">{props.campus.city}</div>
            </Show>
          </div>
        </div>
        <Button variant="ghost" size="icon" class="h-8 w-8" onClick={removeCampus}>
          <Trash2 class="h-4 w-4" />
        </Button>
      </div>
      <RoomsManager orgId={props.orgId} campusId={props.campus.id} />
    </div>
  );
}

export function CampusesTab(props: { orgId: string }) {
  const { t } = useI18n();
  const campuses = useCampuses(() => props.orgId);
  const { drizzle } = useAcademyWrite();
  const [name, setName] = createSignal("");
  const [city, setCity] = createSignal("");

  const addCampus = async (e: Event) => {
    e.preventDefault();
    const db = drizzle();
    if (!db || !name().trim()) {
      return;
    }
    try {
      await createCampusMutation(db, props.orgId, { name: name(), city: city() || null });
      setName("");
      setCity("");
      showToast(t(msg`Campus created`), "success");
    } catch (err) {
      console.error("Error creating campus:", err);
      showToast(t(msg`Error creating campus`), "error");
    }
  };

  return (
    <Card>
      <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle class="text-lg font-bold">{t(msg`Campuses`)}</CardTitle>
        <Building2 class="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent class="space-y-4">
        <form
          onSubmit={addCampus}
          class="flex flex-col gap-2 sm:flex-row"
          data-testid="campus-form"
        >
          <TextField
            class="flex-1"
            placeholder={t(msg`Campus name`)}
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
          />
          <TextField
            class="flex-1"
            placeholder={t(msg`City`)}
            value={city()}
            onInput={(e) => setCity(e.currentTarget.value)}
          />
          <Button type="submit" class="gap-2" disabled={!name().trim()} data-testid="campus-create">
            <Plus class="h-4 w-4" /> {t(msg`Add campus`)}
          </Button>
        </form>

        <Show when={!campuses.isLoading} fallback={<Skeleton class="h-24 w-full rounded-lg" />}>
          <div class="space-y-3">
            <For
              each={campuses.data ?? []}
              fallback={
                <div class="py-8 text-center text-sm text-muted-foreground">
                  {t(msg`No campuses yet`)}
                </div>
              }
            >
              {(c) => <CampusCard campus={c} orgId={props.orgId} />}
            </For>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}
